import * as React from 'react';
import moment from 'moment';
import { shallowEqual } from 'react-redux';
import { Button, Container } from 'react-bootstrap';

import { CopyButton } from '../../../components/CopyButton';
import { OscillometryOverreadModal, OverreadSubmission } from '../Modals/OscillometryOverreadModal';
import { GroupedOscillometryMeasurement } from '../Views/OscillometryDetailedView';

import { useAppSelector } from '../../../redux/store';
import { getCurrentOrganization } from '../../../libs/dataLib';
import { OrganizationType } from '../../../Types/Common/Types';

const { useMemo, useState } = React;

interface OscillometryDetailedHeaderProps {
  groupedMeasurement: GroupedOscillometryMeasurement;
  isTableView: boolean;
  toggleTableView: () => void;
  handleClickGeneratePdf: () => void;
  saveOverread: (overread: OverreadSubmission) => Promise<void>;
  isReadOnly: boolean | undefined;
}

export const OscillometryDetailedHeader: React.FC<OscillometryDetailedHeaderProps> = ({
  groupedMeasurement,
  isTableView,
  toggleTableView,
  handleClickGeneratePdf,
  saveOverread,
  isReadOnly
}) => {
  const translationText = useAppSelector(state => state.translations, shallowEqual);
  const authenticatedProvider = useAppSelector(state => state.provider, shallowEqual);
  const { patientName } = useAppSelector(state => state.patient, shallowEqual);
  let organization: OrganizationType = useAppSelector(state => authenticatedProvider.fullAdmin ? state.organizations.detailedOrganizations[authenticatedProvider.viewingAs.organizationID] : state.organizationsForProvider.detailedOrganizationsForProvider[authenticatedProvider.viewingAs.organizationID]);
  const { detailedOrganizationsForProvider } = useAppSelector(state => state.organizationsForProvider, shallowEqual);
  const { detailedOrganizations } = useAppSelector(state => state.organizations, shallowEqual);
  organization = getCurrentOrganization(authenticatedProvider.viewingAs, authenticatedProvider.fullAdmin ? detailedOrganizations : detailedOrganizationsForProvider);
  const [showOverreadModal, setShowOverreadModal] = useState(false);

  const dataPatientName = useMemo(() => !patientName?.name || patientName?.name?.includes(':') ? patientName?.spirometer[0] : patientName.name, [patientName]);

  const bestMeasurements = groupedMeasurement.bestMeasurements;
  const waveformCount = groupedMeasurement.waveforms.length;

  const date = bestMeasurements
    ? moment.parseZone(bestMeasurements.roundtripTime).format(authenticatedProvider.dateFormat?.toUpperCase() ?? 'MM/DD/YYYY')
    : '';

  const time = useMemo(() => {
    if (!bestMeasurements) return '';
    return new Intl.DateTimeFormat(authenticatedProvider?.locale ?? 'en-US', {
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: 'short'
    }).format(new Date(bestMeasurements.roundtripTime));
  }, [bestMeasurements, authenticatedProvider]);

  const existingOverread = bestMeasurements?.overread;
  const shouldShowOverreadButton = (organization.featureSet.enableOverread || (!!organization.featureSet.enableOverreadReadOnly && existingOverread));
  const overreadReadOnly = !!organization.featureSet.enableOverreadReadOnly || (!!organization.featureSet.enableOverread && !!isReadOnly);

  const getVariant = (acceptability: OverreadSubmission['acceptability'] | undefined) => {
    switch (acceptability) {
      case 'Accept':
        return 'success';
      case 'Borderline Accept':
        return 'warning';
      case 'Reject':
        return 'danger';
      default:
        return 'outline-light';
    };
  };

  return (
    <>
      <div style={{ borderBottom: '2px solid #3C89E8', display: 'flex' }}>
        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginRight: '3rem' }}>
          <span>{dataPatientName}</span>
        </div>
      </div>
      <Container className="statsSeries">
        <h3>
          {translationText.oscillometry || 'Oscillometry'}: {waveformCount} {waveformCount === 1 ? translationText.measurement || 'Measurement' : translationText.measurements || 'Measurements'}; {date}, {time}
        </h3>

        <div className="d-flex justify-content-between align-items-center">
          <Button
            id={`table-view-button-${groupedMeasurement.testId}`}
            className="shadow-none table-view-button"
            size="sm"
            variant={isTableView ? "light" : "outline-light"}
            onClick={toggleTableView}
          >
            {" "}
          </Button>
          <label htmlFor={`table-view-button-${groupedMeasurement.testId}`} className="mr-4" role="button">
            {translationText.tableView || 'Table View'}
          </label>

          {shouldShowOverreadButton &&
            <Button
              variant={getVariant(existingOverread?.acceptability)}
              className="btn-report px-3 mr-2"
              onClick={() => setShowOverreadModal(true)}
            >
              {existingOverread
                ? `${translationText.overread || 'Overread'}: ${existingOverread.acceptability}`
                : translationText.overreadSession || 'Overread Session'
              }
            </Button>
          }

          <Button
            variant="outline-light"
            className="btn-report px-3"
            id="pdf-report-button"
            onClick={handleClickGeneratePdf}
          >
            {translationText.viewThorasysPdf || 'View Thorasys PDF'}
          </Button>

          <CopyButton
            textToCopy={groupedMeasurement.testId}
            tooltipText={`${translationText.copy || 'Copy'} ${translationText.session || 'Session'} ID`}
            id="oscillometry-session-id"
            color="white"
          />
        </div>

        <OscillometryOverreadModal
          show={showOverreadModal}
          setShow={setShowOverreadModal}
          bestMeasurements={bestMeasurements}
          isReadOnly={overreadReadOnly}
          onSave={saveOverread}
        />
      </Container>
    </>
  );
};